import React, { useState, useEffect } from 'react';
import { Sparkles, RefreshCw, ArrowRight } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '../../../../../lib/supabase';

// Import design system and components
import {
  getSchoolColor,
  getCategoryColors,
  getCollegeName,
  formatPercentage,
  formatCurrency,
  formatArrayField,
  calculatePositionScore,
  getPositionLabel
} from '../../shared/designSystem';

import SchoolCard from '../../shared/SchoolCard';
import PositionIndicator from '../../shared/PositionIndicator';
import ComparisonTable from '../../shared/ComparisonTable';
import AdvantagesPanel from '../../shared/AdvantagesPanel';

const CompetitivePositionTab = ({
  colleges = [],
  currentUser,
  portfolioLocked = false,
  studentProfile = {},
  onDataRefresh,
  onDeleteCollege
}) => {
  const [generatingAnalysis, setGeneratingAnalysis] = useState(false);
  const [competitivePositions, setCompetitivePositions] = useState({});
  const [advantages, setAdvantages] = useState({});

  // Load cached data on mount
  useEffect(() => {
    const loadCachedData = () => {
      console.log('🔍 [LOAD] Loading cached data from colleges prop:', colleges.length, 'colleges');
      const cached = {};
      const cachedYourAdvantages = {};
      const cachedAspectsToAddress = {};

      colleges.forEach(college => {
        const name = getCollegeName(college);
        console.log(`📦 [${name}] Full college object:`, {
          competitive_position: college.competitive_position,
          student_advantages: college.student_advantages,
          aspects_to_address: college.aspects_to_address,
          // Log raw values to see what's actually there
          raw_student_advantages: JSON.stringify(college.student_advantages),
          raw_aspects_to_address: JSON.stringify(college.aspects_to_address)
        });

        if (college.competitive_position) {
          cached[name] = college.competitive_position;
        }

        // Use student_advantages as the primary source
        if (college.student_advantages) {
          cachedYourAdvantages[name] = college.student_advantages;
          console.log(`  ✓ Found student_advantages for ${name}:`, college.student_advantages);
        } else {
          console.log(`  ✗ No student_advantages for ${name}`);
        }

        // Check aspects_to_address field
        if (college.aspects_to_address) {
          cachedAspectsToAddress[name] = college.aspects_to_address;
          console.log(`  ✓ Found aspects_to_address for ${name}:`, college.aspects_to_address);
        } else {
          console.log(`  ✗ No aspects_to_address for ${name}`);
        }
      });

      console.log('📊 [CACHE SUMMARY]');
      console.log('  Competitive positions found:', Object.keys(cached));
      console.log('  Your advantages found:', Object.keys(cachedYourAdvantages));
      console.log('  Aspects to address found:', Object.keys(cachedAspectsToAddress));

      if (Object.keys(cached).length > 0) {
        console.log('✅ [STATE] Setting competitive positions to state:', cached);
        setCompetitivePositions(cached);
      } else {
        console.log('⚠️ [STATE] No competitive positions to set');
      }

      // Set advantages with dual-perspective format
      const advantagesData = {
        yourAdvantages: cachedYourAdvantages,
        aspectsToAddress: cachedAspectsToAddress
      };

      console.log('📤 [STATE] Setting advantages to state:', advantagesData);
      setAdvantages(advantagesData);
    };

    loadCachedData();
  }, [colleges]);

  // Generate competitive analysis
  const generateAnalysis = async () => {
    setGeneratingAnalysis(true);
    try {
      // Step 1: Generate competitive positions
      const positionsResponse = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'generate-competitive-position',
          colleges: colleges.map(c => ({
            id: c.id,
            name: getCollegeName(c),
            acceptance_rate: c.global_colleges?.acceptance_rate,
            sat_range: c.global_colleges?.sat_range,
            category: c.category,
            ...c.global_colleges
          })),
          studentProfile,
          userId: currentUser?.id
        })
      });

      if (!positionsResponse.ok) throw new Error('Failed to generate positions');

      const { analysis: positionsAnalysis } = await positionsResponse.json();
      console.log('Received competitive positions:', positionsAnalysis);

      if (positionsAnalysis?.positions) {
        setCompetitivePositions(positionsAnalysis.positions);

        // Save positions to database for each college
        for (const college of colleges) {
          const collegeName = getCollegeName(college);
          const positionData = positionsAnalysis.positions[collegeName];

          if (positionData) {
            try {
              console.log(`Saving position for ${collegeName} (ID: ${college.id}):`, positionData);
              const { data, error } = await supabase
                .from('college_journey_colleges')
                .update({
                  competitive_position: positionData,
                  updated_at: new Date().toISOString()
                })
                .eq('id', college.id)
                .select();

              if (error) {
                console.error(`❌ Error saving position for ${collegeName}:`, error);
              } else {
                console.log(`✅ Successfully saved position for ${collegeName}`, data);
              }
            } catch (saveError) {
              console.error(`❌ Failed to save position for ${collegeName}:`, saveError);
            }
          }
        }
      }

      // Step 2: Generate strategic advantages
      const advantagesResponse = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'generate-comparison-advantages',
          colleges: colleges.map(c => ({
            id: c.id,
            name: getCollegeName(c),
            ...c.global_colleges
          })),
          studentProfile,
          userId: currentUser?.id
        })
      });

      if (advantagesResponse.ok) {
        const result = await advantagesResponse.json();
        console.log('🎯 [API RESPONSE] Received advantages response:', result);

        // New dual-perspective format: { yourAdvantages: {...}, aspectsToAddress: {...} }
        let yourAdvantages = {};
        let aspectsToAddress = {};

        if (result.yourAdvantages || result.aspectsToAddress) {
          console.log('✅ [PARSE] Using new dual-perspective format');
          yourAdvantages = result.yourAdvantages || {};
          aspectsToAddress = result.aspectsToAddress || {};
          console.log('  yourAdvantages:', Object.keys(yourAdvantages));
          console.log('  aspectsToAddress:', Object.keys(aspectsToAddress));
        } else {
          // Legacy format handling
          const legacyData = result.advantages;

          if (legacyData?.raw && legacyData?.error) {
            console.log('Parsing legacy advantages from raw field...');
            try {
              let jsonString = legacyData.raw.trim();
              const lastBraceIndex = jsonString.lastIndexOf('}');
              if (lastBraceIndex !== -1) {
                jsonString = jsonString.substring(0, lastBraceIndex + 1);
              }

              const parsedData = JSON.parse(jsonString);
              console.log('Parsed data:', parsedData);

              // Check if parsed data has new format
              if (parsedData.yourAdvantages || parsedData.aspectsToAddress) {
                yourAdvantages = parsedData.yourAdvantages || {};
                aspectsToAddress = parsedData.aspectsToAddress || {};
              } else if (parsedData.advantages) {
                // Old single-perspective format
                yourAdvantages = parsedData.advantages;
              }
            } catch (parseError) {
              console.error('Failed to parse advantages raw data:', parseError);
              toast.error('Failed to parse advantages data');
              return;
            }
          } else if (legacyData) {
            // Direct legacy advantages object
            console.log('Using legacy direct advantages data');
            yourAdvantages = legacyData;
          }
        }

        // Set state with both perspectives
        const advantagesData = { yourAdvantages, aspectsToAddress };
        console.log('📤 [STATE UPDATE] Setting advantages state:', advantagesData);
        setAdvantages(advantagesData);

        // Save both to database
        console.log('💾 [DB SAVE] Starting database saves for', colleges.length, 'colleges');
        for (const college of colleges) {
          const collegeName = getCollegeName(college);
          const advantagesList = yourAdvantages[collegeName];
          const aspectsList = aspectsToAddress[collegeName];

          console.log(`  [${collegeName}] Preparing to save:`, {
            hasAdvantages: !!advantagesList,
            hasAspects: !!aspectsList,
            advantagesCount: advantagesList?.length,
            aspectsCount: aspectsList?.length
          });

          if (advantagesList || aspectsList) {
            try {
              const updateData = {
                updated_at: new Date().toISOString()
              };

              if (advantagesList) updateData.student_advantages = advantagesList;
              if (aspectsList) updateData.aspects_to_address = aspectsList;

              console.log(`  [${collegeName}] Saving to DB (ID: ${college.id}):`, updateData);

              const { data, error } = await supabase
                .from('college_journey_colleges')
                .update(updateData)
                .eq('id', college.id)
                .select();

              if (error) {
                console.error(`❌ [DB ERROR] Error saving for ${collegeName}:`, error);
              } else {
                console.log(`✅ [DB SUCCESS] Successfully saved for ${collegeName}. Returned data:`, data);
              }
            } catch (saveError) {
              console.error(`❌ [DB EXCEPTION] Failed to save for ${collegeName}:`, saveError);
            }
          } else {
            console.log(`  [${collegeName}] Skipping - no data to save`);
          }
        }

        toast.success('Strategic insights generated');

        // Refresh data to load newly saved values
        if (onDataRefresh) {
          console.log('🔄 [REFRESH] Calling onDataRefresh to reload college data...');
          await onDataRefresh();
          console.log('✅ [REFRESH] Data refresh completed');
        } else {
          console.warn('⚠️ [REFRESH] No onDataRefresh callback provided');
        }
      } else {
        toast.error('Failed to generate advantages');
      }
    } catch (error) {
      console.error('Error generating analysis:', error);
      toast.error('Failed to generate analysis');
    } finally {
      setGeneratingAnalysis(false);
    }
  };

  // Define comparison table metrics
  const comparisonMetrics = [
    { label: 'Ranking', field: 'global_colleges.usnwr_ranking' },
    { label: 'Acceptance Rate', field: 'global_colleges.acceptance_rate', format: 'percentage' },
    { label: 'SAT Range', field: 'global_colleges.sat_range' },
    {
      label: 'Your SAT',
      highlight: true,
      getValue: (college) => {
        const studentSAT = studentProfile?.sat_total || 'Unknown';
        return `${studentSAT} ✓`;
      }
    },
    { label: 'Total Cost', field: 'global_colleges.total_cost', format: 'currency' },
    { label: 'Core Values', field: 'global_colleges.core_values', format: 'array', maxItems: 4 }
  ];

  // Create AI update handlers for each college
  const aiUpdateHandlers = {};
  colleges.forEach(college => {
    const collegeName = getCollegeName(college);
    if (!competitivePositions[collegeName]) {
      aiUpdateHandlers[college.id] = {
        tooltip: 'Generate competitive analysis',
        onClick: generateAnalysis,
        isLoading: generatingAnalysis
      };
    }
  });

  return (
    <div className="space-y-8">
      {/* School Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {colleges.map((college, idx) => {
          const colors = getSchoolColor(idx);
          const collegeName = getCollegeName(college);
          const positionScore = calculatePositionScore(college, competitivePositions);
          const positionLabel = getPositionLabel(college, competitivePositions);
          const percentile = competitivePositions[collegeName]?.percentile || Math.round(positionScore * 20);

          return (
            <SchoolCard
              key={college.id || idx}
              college={college}
              index={idx}
              category={college.category}
              showCategory={true}
              showArrow={true}
              onDelete={onDeleteCollege}
            >
              {/* Position Indicator */}
              <PositionIndicator
                index={idx}
                percentage={percentile}
                label={`${percentile}th percentile`}
                title="Your Position"
                description="Among admitted students with similar profiles"
              />

              {/* Quick Stats */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500/90">Acceptance Rate</span>
                  <span className="text-xs font-bold text-slate-900">
                    {formatPercentage(college.global_colleges?.acceptance_rate)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500/90">Your SAT Position</span>
                  <span className="text-xs font-bold text-slate-900">
                    {studentProfile?.sat_total|| Unknown} ({college.global_colleges?.sat_range || 'N/A'})
                  </span>
                </div>
              </div>

              {/* Footer */}
              <div className={`-mx-6 -mb-6 mt-6 ${colors.bg} px-6 py-3 border-t border-slate-400`}>
                <p className="text-xs font-medium text-slate-700">
                  {positionLabel} • {formatPercentage(college.global_colleges?.acceptance_rate)} acceptance
                </p>
              </div>
            </SchoolCard>
          );
        })}
      </div>

      {/* Comparison Table */}
      <ComparisonTable
        colleges={colleges}
        metrics={comparisonMetrics}
        aiUpdateHandlers={aiUpdateHandlers}
      />

      {/* Your Advantages Section */}
      <AdvantagesPanel
        colleges={colleges}
        advantagesData={advantages}
        onGenerate={generateAnalysis}
        isGenerating={generatingAnalysis}
      />
    </div>
  );
};

export default CompetitivePositionTab;
